/**
 * Copyright 2016 xdot GmbH
 * Author: Marius Runde
 * Date: 21.04.2016
 *
 */

'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { FormControl, Button } from 'external/react-bootstrap';
import {
    AppContext,
    DataUtil,
    translation,
    FormActions,
    wrapWithCommonFormContainer
} from 'plugin/liferay-widgets-react-web';
import {
    FliwasFormGrid,
    FliwasFormGroup,
    FliwasFormHeading,
    FliwasSelect,
    FormValidationUtil,
    NumberSelect
} from 'plugin/base-portlet';
import { GeometryInput, getMapConfigStore } from 'plugin/map-portlet';
import { getFliwasOrganizationStore } from '../stores/StoreFactory';
import TerritorialAreaSelect from '../components/TerritorialAreaSelect';
import FliwasMultiOrganizationConfigForm from '../widgets/FliwasMultiOrganizationConfigForm.jsx';
import { ClassNames } from '../constants/Constants';

let FliwasStore = getFliwasOrganizationStore(AppContext);
const MapConfigStore = getMapConfigStore(AppContext);
const FliwasMultiOrganizationConfigFormHoc = wrapWithCommonFormContainer(FliwasMultiOrganizationConfigForm);

const validate = formData => {
    let titleValid, boundingBoxValid, federalStateValid, territoryIdsValid, geometryValid;

    titleValid = FormValidationUtil.validateStringMinLengthMaxLength(formData.title, 1, 75, 'Titel');

    if (!formData.boundingBox) {
        boundingBoxValid = 'Bitte einen Standardkartenausschnitt zeichnen!';
    }

    if (formData.administrativeRank > 0 && formData.administrativeRank !== 99 && !(formData.territoryIds && formData.territoryIds.length > 0)) {
        territoryIdsValid = 'Bitte Gebiet auswählen!';
    }

    if (formData.administrativeRank === 99 && !formData.geometry) {
        geometryValid = 'Bitte Gebiet/Position zeichnen!';
    }

    if (!(formData.federalState && formData.federalState.length > 0)) {
        federalStateValid = 'Bitte Bundesland auswählen!';
    }

    return {
        title: titleValid,
        boundingBox: boundingBoxValid,
        territoryIds: territoryIdsValid,
        geometry: geometryValid,
        federalState: federalStateValid,
    };
};

const ADMINISTRATIVE_RANK_MAP = {
    3: 'rp',
    4: 'district',
    6: 'commune'
};

const drawOptions = {
    circle: false,
    circlemarker: false,
    marker: false,
    polyline: false,
    polygon: true,
    rectangle: true,
};

class FliwasOrganizationNewForm extends React.Component {

    constructor(props) {
        super(props);
        this.props.onChange(this.props.formData, validate(this.props.formData));

        this.DATA = {
            SITUATION_MAPS_GROUP_CHOOSER: this.props.context.contextId + 'SituationMapsGroupChooser',
        };
    }

    onHandleTerritoryChange(propName, geometry) {
        const newData = {
            ...this.props.formData,
            [propName]: geometry
        };
        this.props.handleGeometryChange(newData, validate(newData));
    }

    onHandleGeometryChange(geometry) {
        const newData = {
            ...this.props.formData,
            geometry: geometry
        };
        this.props.handleGeometryChange(newData, validate(newData));
    }

    onHandleBoundingBoxChange(boundingBox) {
        const newData = {
            ...this.props.formData,
            boundingBox
        };
        this.props.handleBoundingBoxChange(newData, validate(newData));
    }

    changeRank(field, value) {
        const formData = this.props.formData,
            item = formData.item || formData;

        let newData;
        if (value === 99) {
            newData = {
                ...item,
                [field]: value,
                territoryIds: []

            };
        } else {
            newData = {
                ...item,
                [field]: value,
                territoryIds: [],
                geometry: undefined
            };
        }

        this.props.onChange(newData, validate(newData));
    }

    handleChange(field, e) {
        this.change(field, e.target.value);
    }

    change(field, value) {
        const formData = this.props.formData;
        const item = formData.item || formData;

        const newData = {
            ...item,
            [field]: value
        };

        this.props.onChange(newData, validate(newData));
    }

    onSituationMapSelectorClick(evt) { // 1018
        FormActions.startEditFormItem(this.DATA.SITUATION_MAPS_GROUP_CHOOSER, {
            activeOrganizationIds: this.props.formData.situationMapGroupIds,
        });

        evt.stopPropagation();
        evt.preventDefault();
    }

    onSubmitSituationMapSelection(data) { // 1018
        console.log('onSubmitConfigForm', data);
        const formData = this.props.formData;
        const item = formData.item || formData;
        const newData = {
            ...item,
            situationMapGroupIds: data.activeOrganizationIds,
        };

        this.props.onChange(newData, validate(newData));
        FormActions.stopEditFormItem(this.DATA.SITUATION_MAPS_GROUP_CHOOSER);
    }

    render() {

        const { formData, formValidation } = this.props,
            geometry = formData && formData.geometry,
            administrativeRank = formData && formData.administrativeRank || 0,
            boundingBox = formData && (formData.boundingBox || geometry);

        let federalStates = DataUtil.asArray(FliwasStore.getRemoteData(this.props.federalStatesDataList)).map((i, k) => {
            return {
                value: i.title,
                label: i.title
            };
        });

        const defaultMapConfig = DataUtil.asItem(MapConfigStore.getDefaultMapConfig());

        return (

            <FliwasFormGrid>
                <FliwasFormHeading title={'Mandanten anlegen'}/>

                <FliwasFormGroup
                    title={translation.trans('title')}
                    formValidation={formValidation}
                    propName={'title'}
                    required
                >
                    <FormControl
                        type="text"
                        required
                        placeholder={translation.trans('title')}
                        value={formData && formData.title ? formData.title : ''}
                        onChange={this.handleChange.bind(this, 'title')}
                    />
                </FliwasFormGroup>


                <FliwasFormGroup
                    title={translation.trans('federalState')}
                    formValidation={formValidation}
                    propName={'federalState'}
                    required
                >
                    <FliwasSelect
                        list={federalStates}
                        label={translation.trans('federal-state')}
                        required
                        onChange={this.handleChange.bind(this, 'federalState')}
                    />
                </FliwasFormGroup>


                <FliwasFormGroup
                    title={translation.trans('boundingBox')}
                    formValidation={formValidation}
                    propName={'boundingBox'}
                    required
                >
                    <GeometryInput
                        hideLabel={true}
                        nameGeometry={'boundingBox'}
                        geometry={boundingBox}
                        type={ClassNames.Organization}
                        handleGeometryChange={this.onHandleBoundingBoxChange.bind(this)}
                        drawOptions={drawOptions}
                        required
                        layerDescriptions={defaultMapConfig && defaultMapConfig.children}
                    />
                </FliwasFormGroup>

                <FliwasFormGroup
                    title={translation.trans('administrativeRank')}
                    formValidation={formValidation}
                    propName={'administrativeRank'}
                >

                    <NumberSelect
                        list={[
                            { value: 3, label: 'Regierungsbezirk' },
                            { value: 4, label: 'Land-/Stadtkreis' },
                            { value: 6, label: 'Stadt/Gemeinde' },
                            { value: 99, label: 'Sonstige' }
                        ]}
                        label={translation.trans('type')}
                        currentValue={administrativeRank}
                        onChange={this.changeRank.bind(this, 'administrativeRank')}
                    />
                </FliwasFormGroup>

                <FliwasFormGroup title={translation.trans('situationMapSelection')}>
                    <div>
                        <Button
                            title={'Mandanten auswählen'}
                            onClick={this.onSituationMapSelectorClick.bind(this)}
                            variant="primary"
                            type="button"
                        >
                            Auswählen
                        </Button>

                        <FliwasMultiOrganizationConfigFormHoc
                            formId={this.DATA.SITUATION_MAPS_GROUP_CHOOSER}
                            onFormSubmit={this.onSubmitSituationMapSelection.bind(this)}
                        />
                    </div>
                </FliwasFormGroup>

                {administrativeRank > 0 &&
                    <FliwasFormGroup
                        title={translation.trans('geometry')}
                        formValidation={formValidation}
                        propName={'geometry'}
                        required
                    >

                        {(administrativeRank !== -1 && administrativeRank !== 99) &&
                            <TerritorialAreaSelect
                                propName={'territoryIds'}
                                formData={formData}
                                type={ADMINISTRATIVE_RANK_MAP[administrativeRank]}
                                onChange={this.onHandleTerritoryChange.bind(this)}
                            />
                        }

                        {(administrativeRank === 99) &&
                            <GeometryInput
                                hideLabel={true}
                                geometry={formData.geometry}
                                type={ClassNames.Organization}
                                handleGeometryChange={this.onHandleGeometryChange.bind(this)}
                                drawOptions={drawOptions}
                                layerDescriptions={defaultMapConfig && defaultMapConfig.children}
                            />
                        }
                    </FliwasFormGroup>}

            </FliwasFormGrid>
        );
    }
}

FliwasOrganizationNewForm.propTypes = {
    formData: PropTypes.object,
    formValidation: PropTypes.object,
    drawOptions: PropTypes.object,
    federalStatesDataList: PropTypes.string,
    handleGeometryChange: PropTypes.func,
    handleBoundingBoxChange: PropTypes.func,
    onChange: PropTypes.func,
    context: PropTypes.shape({
        contextId: PropTypes.string,
    })
};

export default FliwasOrganizationNewForm;



// WEBPACK FOOTER //
// ./js/components/FliwasOrganizationNewForm.jsx